import { createTheme, Theme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";
import { createThemeByMode, PaletteMode } from "@rusha/shared-theme";

/**
 * Landing page theme — extends the shared brand-spec theme with landing-only
 * component overrides (glassmorphism AppBar, hover-lift cards, gradient
 * primary button, brand scrollbars).
 *
 * The brand palette (Flow / Deploy / Deep Space / Infrastructure / Accent
 * Purple) lives in `@rusha/shared-theme` and is shared with `src/ui`.
 */

const landingComponents = (mode: PaletteMode): Theme["components"] => ({
  MuiAppBar: {
    styleOverrides: {
      colorPrimary:
        mode === "dark"
          ? {
              backgroundColor: alpha("#0c0a18", 0.85),
              color: "#f1f5f9",
              borderBottom: `1px solid ${alpha("#ffffff", 0.08)}`,
              backdropFilter: "blur(8px)",
            }
          : {
              backgroundColor: alpha("#ffffff", 0.85),
              color: "#0f172a",
              borderBottom: `1px solid ${alpha("#0f172a", 0.08)}`,
              backdropFilter: "blur(8px)",
            },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 12,
        boxShadow:
          mode === "dark"
            ? `0 4px 16px ${alpha("#000000", 0.3)}`
            : "0 4px 6px -1px rgba(0,0,0,0.08), 0 2px 4px -1px rgba(0,0,0,0.04)",
        overflow: "hidden",
        border: `1px solid ${alpha(mode === "dark" ? "#ffffff" : "#0f172a", 0.08)}`,
        transition:
          "transform 200ms ease, box-shadow 200ms ease, border-color 200ms ease",
        "&:hover": {
          transform: "translateY(-2px)",
          borderColor: alpha("#3b82f6", 0.4),
          boxShadow:
            mode === "dark"
              ? `0 12px 32px ${alpha("#000000", 0.5)}`
              : `0 12px 24px ${alpha("#3b82f6", 0.12)}`,
        },
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      filled: {
        backgroundColor: alpha("#3b82f6", 0.08),
        "&:hover": { backgroundColor: alpha("#3b82f6", 0.14) },
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      containedPrimary: {
        background: `linear-gradient(135deg, #3b82f6 0%, #7c3aed 100%)`,
        boxShadow: `0 4px 14px ${alpha("#3b82f6", 0.35)}`,
        "&:hover": {
          background: `linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)`,
          boxShadow: `0 6px 20px ${alpha("#3b82f6", 0.45)}`,
        },
      },
    },
  },
  MuiCssBaseline: {
    styleOverrides: {
      body: {
        scrollbarColor:
          mode === "dark" ? `${alpha("#ffffff", 0.2)} #0c0a18` : undefined,
      },
    },
  },
});

/**
 * Create a landing theme for the specified mode. Re-uses the shared
 * brand-spec palette and layers landing-only component overrides on top.
 */
export const createLandingThemeByMode = (mode: PaletteMode): Theme => {
  const base = createThemeByMode(mode);
  return createTheme(base, {
    components: landingComponents(mode),
  });
};

/**
 * Legacy default export (light theme) — kept for backward compatibility with
 * `__tests__/theme.test.ts`. Prefer `createLandingThemeByMode`.
 */
const theme = createLandingThemeByMode("light");
export default theme;
